// AttributionPrompt — "How did you hear about Parcely?" then "What are you
// here to do?", asked once.
//
// Shown on the first signed-in load after the county picker is out of the
// way, while profiles.signup_source_asked_at is NULL (GET /profile/counties
// carries the flag). Each step is one tap and is saved as soon as it is
// answered, so a user who closes after the first tap keeps that answer.
// "Skip" on the first step records "skipped" (so the prompt never comes back)
// and still moves on to the intent question: 3 of the first 4 real users
// skipped the channel question and so never saw the intent one, the more
// useful of the two. Escape or a backdrop click records "skipped" and closes.
//
// Step 1 (channel) is the only acquisition signal our own database keeps:
// UTMs never reach us from a letter, a REIA group or a referral.
// Step 2 (intent, migration 246) is a self-reported segment the funnel can be
// split by. It is not a persona; personas come from interviews.
//
// No free text except behind "Somewhere else", capped server-side at 140.

import React, { useState } from "react";
import { capture, setUserProperties } from "../lib/analytics";
import { putProfileAttribution, putProfileIntent, SignupIntent, SignupSource } from "../api";
import { useFocusTrap } from "../hooks/useFocusTrap";

interface AttributionPromptProps {
  /** Called when the user is finished (answered or skipped), or dismissed
   *  after a failed save. The parent hides the prompt either way. */
  onDone: () => void;
}

export const ATTRIBUTION_CHOICES: ReadonlyArray<{ code: Exclude<SignupSource, "skipped">; label: string }> = [
  { code: "facebook_reia", label: "Facebook or a REIA group" },
  { code: "google", label: "Google search" },
  { code: "letter", label: "A letter in the mail" },
  { code: "referral", label: "A friend or colleague" },
  { code: "agent", label: "A real-estate agent" },
  { code: "other", label: "Somewhere else" },
];

export const INTENT_CHOICES: ReadonlyArray<{ code: SignupIntent; label: string }> = [
  { code: "auction_bidder", label: "Bid at foreclosure auctions" },
  { code: "wholesaler", label: "Find off-market sellers to wholesale" },
  { code: "flipper_landlord", label: "Buy to flip or rent" },
  { code: "agent", label: "I'm an agent working with buyers" },
  { code: "researching", label: "Just researching" },
];

const AttributionPrompt: React.FC<AttributionPromptProps> = ({ onDone }) => {
  const [step, setStep] = useState<"source" | "intent">("source");
  const [picked, setPicked] = useState<SignupSource | null>(null);
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitSource = async (source: SignupSource, text = "", thenIntent = source !== "skipped") => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await putProfileAttribution(source, text);
      // The server captures signup_source_answered too; this client-side
      // copy keeps the PostHog person property fresh for this session.
      setUserProperties({ signup_source: source });
      capture("attribution_prompt_answered", { source, has_detail: text.trim().length > 0 });
      setBusy(false);
      if (thenIntent) {
        setStep("intent");
      } else {
        onDone();
      }
    } catch {
      setBusy(false);
      setError("Couldn't save that. You can close this and carry on.");
    }
  };

  const submitIntent = async (intent: SignupIntent) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await putProfileIntent(intent);
      setUserProperties({ signup_intent: intent });
      capture("intent_prompt_answered", { intent });
      onDone();
    } catch {
      setBusy(false);
      setError("Couldn't save that. You can close this and carry on.");
    }
  };

  // Escape and the backdrop mean "no more questions". On the first step that
  // is recorded as "skipped" so the prompt never returns; on the second the
  // channel is already saved, so it just closes.
  const skip = () => {
    if (error) {
      onDone();
      return;
    }
    if (step === "source") {
      void submitSource("skipped");
    } else {
      capture("intent_prompt_skipped");
      onDone();
    }
  };
  const trapRef = useFocusTrap<HTMLDivElement>({ onEscape: skip });

  // The Skip button: on the first step, skip only this question.
  const skipButton = () => {
    if (!error && step === "source") {
      void submitSource("skipped", "", true);
      return;
    }
    skip();
  };

  const choose = (code: SignupSource) => {
    if (code === "other") {
      setPicked("other");
      return;
    }
    void submitSource(code);
  };

  return (
    <div className="auth-backdrop" onClick={skip}>
      <div
        className="upgrade-modal attribution-modal"
        onClick={(e) => e.stopPropagation()}
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="attribution-title"
      >
        <h2 className="upgrade-title" id="attribution-title">
          {step === "source" ? "One quick question" : "And one more"}
        </h2>
        {step === "source" ? (
          <>
            <p className="upgrade-reason">How did you hear about Parcely?</p>
            <div className="attribution-choices" role="group" aria-label="How did you hear about Parcely?">
              {ATTRIBUTION_CHOICES.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  className={`attribution-chip${picked === c.code ? " attribution-chip--active" : ""}`}
                  onClick={() => choose(c.code)}
                  disabled={busy}
                >
                  {c.label}
                </button>
              ))}
            </div>
            {picked === "other" && (
              <form
                className="attribution-other"
                onSubmit={(e) => {
                  e.preventDefault();
                  void submitSource("other", detail);
                }}
              >
                <label htmlFor="attribution-detail" className="attribution-other-label">
                  Where?
                </label>
                <input
                  id="attribution-detail"
                  className="county-picker-search"
                  type="text"
                  maxLength={140}
                  value={detail}
                  onChange={(e) => setDetail(e.target.value)}
                  placeholder="A podcast, a courthouse, a newsletter…"
                  autoFocus
                />
                <button type="submit" className="upgrade-btn-primary" disabled={busy}>
                  {busy ? "Saving…" : "Save"}
                </button>
              </form>
            )}
          </>
        ) : (
          <>
            <p className="upgrade-reason">What are you here to do?</p>
            <div className="attribution-choices" role="group" aria-label="What are you here to do?">
              {INTENT_CHOICES.map((c) => (
                <button key={c.code} type="button" className="attribution-chip" onClick={() => void submitIntent(c.code)} disabled={busy}>
                  {c.label}
                </button>
              ))}
            </div>
          </>
        )}
        {error && (
          <p className="county-picker-error" role="alert">
            {error}
          </p>
        )}
        <div className="upgrade-actions">
          <button type="button" className="upgrade-btn-secondary" onClick={skipButton} disabled={busy && !error}>
            {error ? "Close" : "Skip"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttributionPrompt;
